import { FC, Fragment, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Combobox, Flex, Text } from '@servicetitan/anvil2';
import { useDependencies } from '@servicetitan/react-ioc';
import {
    EquipmentManualSelectStore,
    ManualEquipmentData,
} from '../store/equipment-manual-select.store';
import {
    EquipmentManufacturer,
    EquipmentModelNumber,
    EquipmentModelSeries,
} from '../models/equipment.models';
import { BottomDrawerSelect } from '../../common/components/bottom-drawer-select/bottom-drawer-select';
import { MessageBubble } from '../../common/components/message-bubble/message-bubble';
import { ModelItem } from './model-item';

export interface EquipmentManualSelectProps {
    isLoading: boolean;
    onSubmit: (data: ManualEquipmentData | undefined) => void;
}

export const EquipmentManualSelect: FC<EquipmentManualSelectProps> = observer(
    ({ isLoading, onSubmit }) => {
        const [
            {
                formState,
                isSearching,
                // --manufacturers
                setManufacturersSearchQuery,
                manufacturersOptions,
                onManufacturerChange,
                // --modelSeries
                onModelSeriesChange,
                modelSeriesOptions,
                setModelSeriesSearchQuery,
                // -- model number
                modelNumberOptions,
                onModelNumberChange,
                setModelNumberSearchQuery,

                getFormData,
                modelNumberSearchInfoText,
            },
        ] = useDependencies<[EquipmentManualSelectStore]>(EquipmentManualSelectStore);

        const [manufacturerDropdownOpen, setManufacturerDropdownOpen] = useState(false);
        const [modelSeriesDropdownOpen, setModelSeriesDropdownOpen] = useState(false);
        const [modelNumberDropdownOpen, setModelNumberDropdownOpen] = useState(false);

        const {
            $: { manufacturerFieldState, modelSeriesFieldState, modelNumberFieldState },
        } = formState;

        const handleManufacturerSelect = (manufacturer?: EquipmentManufacturer | null) => {
            onManufacturerChange(manufacturer ?? undefined);
            setManufacturerDropdownOpen(false);
        };

        const handleModelSeriesSelect = (modelSeries?: EquipmentModelSeries | null) => {
            onModelSeriesChange(modelSeries ?? undefined);
            setModelSeriesDropdownOpen(false);
        };

        const handleModelNumberSelect = (modelNumber?: EquipmentModelNumber | null) => {
            onModelNumberChange(modelNumber ?? undefined);
            setModelNumberDropdownOpen(false);
        };

        const handleSubmit = async () => {
            const data = await getFormData();
            onSubmit(data);
        };

        return (
            <Fragment>
                <MessageBubble
                    isMine={false}
                    style={{
                        display: 'block',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <Flex flex={1} gap="4" direction="column" className="w-100-i">
                        <Text size="small">Select equipment manually:</Text>
                        <div onClick={() => setManufacturerDropdownOpen(true)}>
                            <Combobox
                                open={false}
                                items={manufacturersOptions ?? []}
                                onChange={handleManufacturerSelect}
                                selectedItem={manufacturerFieldState.$}
                                itemToString={item => item?.label ?? ''}
                                itemToKey={item => (item ? item.id : null)}
                            >
                                <Combobox.SelectTrigger
                                    label="Manufacturer"
                                    placeholder="Search manufacturer..."
                                    value={manufacturerFieldState.$?.label ?? ''}
                                />
                            </Combobox>
                        </div>
                        <div onClick={() => setModelSeriesDropdownOpen(true)}>
                            <Combobox
                                open={false}
                                items={modelSeriesOptions ?? []}
                                onChange={handleModelSeriesSelect}
                                selectedItem={modelSeriesFieldState.$}
                                itemToString={item => item?.label ?? ''}
                                itemToKey={item => (item ? item.id : null)}
                            >
                                <Combobox.SelectTrigger
                                    label="Model Series"
                                    placeholder="Search model line..."
                                    value={modelSeriesFieldState.$?.label ?? ''}
                                />
                            </Combobox>
                        </div>
                        <div onClick={() => setModelNumberDropdownOpen(true)}>
                            <Combobox
                                open={false}
                                items={modelNumberOptions ?? []}
                                onChange={handleModelNumberSelect}
                                selectedItem={modelNumberFieldState.$}
                                itemToString={item => item?.label ?? ''}
                                itemToKey={item => (item ? item.id : null)}
                            >
                                <Combobox.SelectTrigger
                                    required
                                    label="Model Number"
                                    placeholder="Search model line..."
                                    error={
                                        modelNumberFieldState.$ &&
                                        !modelNumberFieldState.$.hasDocumentation &&
                                        'No documentation available yet, responses may be inaccurate'
                                    }
                                    value={modelNumberFieldState.$?.label ?? ''}
                                />
                            </Combobox>
                        </div>
                        <Button
                            disabled={formState.hasFormError}
                            appearance="primary"
                            loading={isLoading}
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>
                    </Flex>
                </MessageBubble>
                <BottomDrawerSelect
                    open={manufacturerDropdownOpen}
                    onClose={() => setManufacturerDropdownOpen(false)}
                    isLoading={isSearching}
                    dropdownOptions={manufacturersOptions}
                    itemToKey={item => item.id}
                    searchPlaceholder="Search manufacturer"
                    onSearchQueryChange={setManufacturersSearchQuery}
                    onChange={handleManufacturerSelect}
                    value={manufacturerFieldState.value}
                />

                <BottomDrawerSelect
                    open={modelSeriesDropdownOpen}
                    onClose={() => setModelSeriesDropdownOpen(false)}
                    isLoading={isSearching}
                    dropdownOptions={modelSeriesOptions}
                    itemToKey={item => item.id}
                    searchPlaceholder="Search model series"
                    onSearchQueryChange={setModelSeriesSearchQuery}
                    onChange={handleModelSeriesSelect}
                    value={modelSeriesFieldState.value}
                />

                <BottomDrawerSelect
                    open={modelNumberDropdownOpen}
                    onClose={() => setModelNumberDropdownOpen(false)}
                    isLoading={isSearching}
                    dropdownOptions={modelNumberOptions}
                    itemToKey={item => item.id}
                    searchPlaceholder="Search model number"
                    onSearchQueryChange={setModelNumberSearchQuery}
                    onChange={handleModelNumberSelect}
                    value={modelNumberFieldState.value}
                    getIsItemErrored={item => !item.hasDocumentation}
                    searchInputInfo={modelNumberSearchInfoText}
                    itemRenderer={item => <ModelItem item={item} />}
                />
            </Fragment>
        );
    }
);
